import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type { CountyFeature } from "./geometry";

/** Rodzaj firmy na megamapie = typ prac z kartoteki (`work_types`) bez projektanta
 * sprawdzającego (sam sprawdzający nie trafia na mapę wcale — backend go pomija). Firma może
 * mieć kilka albo żadnego (pusta lista = „bez typu", widoczna tylko pod „Wszyscy");
 * „Geodeta z WPW" ma zawsze też „surveyor". */
export type AreaMapKind = "wpw" | "surveyor" | "surveyor_wpw" | "coordinator";

export const AREA_MAP_KINDS: AreaMapKind[] = ["wpw", "surveyor", "surveyor_wpw", "coordinator"];

export const KIND_LABELS: Record<AreaMapKind, string> = {
  wpw: "WPW",
  surveyor: "Geodeta",
  surveyor_wpw: "Geodeta z WPW",
  coordinator: "Koordynator",
};

/** Województwo z obszaru działania firmy (mirrors ContractorAreaMapView). */
export interface AreaMapArea {
  voivodeship: string;
  voivodeship_label: string;
  /** true = całe województwo (brak zawężenia do powiatów). */
  whole: boolean;
  /** Zawężające kody powiatów — puste, gdy `whole`. */
  counties: string[];
  county_names: string[];
  county_total: number;
}

/** Grupa aktywnych zamówień pod nazwą firmy: WPW, mapy (MDCP) albo prowadzenie projektu. */
export type OrderGroup = "wpw" | "maps" | "lead";

export const ORDER_GROUP_LABELS: Record<OrderGroup, string> = {
  wpw: "WPW",
  maps: "Mapy",
  lead: "Prowadzenie",
};

/** Kolejność grup zamówień — na liście i w dymku powiatu. */
export const ORDER_GROUPS: OrderGroup[] = ["wpw", "maps", "lead"];

export interface OrderCounty {
  /** Kod TERYT powiatu; pusty = powiatu zamówienia nie da się ustalić. */
  teryt: string;
  name: string;
  voivodeship_label: string;
  /** Liczba aktywnych zamówień grupy w tym powiecie. */
  count: number;
  /** Ile z nich firma już przyjęła (status po „Wysłane”). */
  accepted: number;
}

export interface OrderGroupSummary {
  /** Liczba zamówień grupy bez powtórzeń (zamówienie w dwóch powiatach liczy się raz). */
  count: number;
  counties: OrderCounty[];
}

/** „Informacje dla koordynatorów" z umowy ramowej firmy (tylko umowy widoczne dla
 * pytającego i nieprzeterminowane) — ikona „?" przy firmie na mapie. */
export interface AreaMapCoordinatorInfo {
  agreement: string;
  title: string;
  text: string;
}

export interface AreaMapContractor {
  id: string;
  name: string;
  short_name: string;
  /** Typy prac z kartoteki; pusta = firma bez typu (jest na mapie tylko pod „Wszyscy"). */
  kinds: AreaMapKind[];
  /** Pełne etykiety typów z kartoteki, w kolejności `kinds`. */
  kind_labels: string[];
  /** Pusta = brak ikony „?". */
  coordinator_info: AreaMapCoordinatorInfo[];
  areas: AreaMapArea[];
  /** Rozwinięta lista kodów TERYT powiatów obszaru (pusta = brak obszaru). */
  county_codes: string[];
  /** Powiaty aktywnych zamówień — grupa bez zamówień nie występuje. */
  orders: Partial<Record<OrderGroup, OrderGroupSummary>>;
}

/** Aktywne zamówienie przy pinezce projektu. */
export interface AreaMapProjectOrder {
  id: string;
  number: string;
  contractor: string;
  group: OrderGroup;
  status: string;
  status_label: string;
  /** Firma już przyjęła zamówienie (status po „Wysłane”). */
  accepted: boolean;
}

/** Pinezka projektu ZE: z aktywnymi zamówieniami firm z megamapy albo aktywny bez nich. */
export interface AreaMapProject {
  connection: string;
  /** Id przedsięwzięcia — link /power-projects/{project}. */
  project: string;
  name: string;
  /** Przedsięwzięcie w statusie „Aktywne” (wstrzymane przychodzą tylko z zamówieniami). */
  active: boolean;
  lat: number;
  lng: number;
  /** Puste = aktywny projekt bez aktywnych zamówień firm z megamapy (szara pinezka). */
  orders: AreaMapProjectOrder[];
}

export interface AreaMapBase {
  contractor: string;
  lat: number;
  lng: number;
  label: string;
}

export const useContractorAreaMap = () =>
  useQuery({
    queryKey: ["contractors", "area-map"],
    queryFn: () =>
      apiClient
        .get<{ data: AreaMapContractor[] }>("/contractors/area-map/")
        .then((r) => r.data.data),
  });

/** Pinezki siedzib — osobne zapytanie, bo backend geokoduje adresy (GUGiK). */
export const useContractorAreaMapBases = () =>
  useQuery({
    queryKey: ["contractors", "area-map", "bases"],
    queryFn: () =>
      apiClient
        .get<{ data: AreaMapBase[] }>("/contractors/area-map/bases/")
        .then((r) => r.data.data),
    staleTime: 10 * 60 * 1000,
  });

/** Pinezki projektów — osobne zapytanie (punkt projektu liczy się z trasy / działek). */
export const useContractorAreaMapProjects = () =>
  useQuery({
    queryKey: ["contractors", "area-map", "projects"],
    queryFn: () =>
      apiClient
        .get<{ data: AreaMapProject[]; meta: { count: number; unlocated: number } }>(
          "/contractors/area-map/projects/",
        )
        .then((r) => ({ projects: r.data.data, unlocated: r.data.meta.unlocated })),
  });

/** Granice 380 powiatów (~350 kB po gzip) — statyczny plik, raz na sesję. */
export const useCountyBoundaries = () =>
  useQuery({
    queryKey: ["geo", "county-boundaries"],
    queryFn: () =>
      apiClient
        .get<{ data: { features: CountyFeature[] } }>("/county-boundaries/")
        .then((r) => r.data.data.features),
    staleTime: Infinity,
    gcTime: Infinity,
  });
